/**
 * card-product-swiper.js
 * -----------------------------------------------
 * Lightweight vanilla JS swiper for Dawn product cards.
 * No Swiper.js library needed.
 *
 * Add this file to your Dawn theme assets folder and
 * include it in theme.liquid before </body>:
 *   {{ 'card-product-swiper.js' | asset_url | script_tag }}
 * -----------------------------------------------
 */

(function () {
  'use strict';

  class ProductCardSwiper {
    constructor(el) {
      this.el = el;
      this.wrapper = el.querySelector('.swiper-wrapper');
      this.slides = Array.from(el.querySelectorAll('.swiper-slide'));
      this.prevBtn = el.querySelector('.card__swiper-btn--prev');
      this.nextBtn = el.querySelector('.card__swiper-btn--next');
      this.pagination = el.querySelector('.card__swiper-pagination');

      if (!this.wrapper || this.slides.length <= 1) {
        if (this.prevBtn) this.prevBtn.style.display = 'none';
        if (this.nextBtn) this.nextBtn.style.display = 'none';
        if (this.pagination) this.pagination.style.display = 'none';
        return;
      }

      this.current = 0;
      this.total = this.slides.length;
      this.isAnimating = false;

      this._fixLayout();
      this._preloadImages();
      this._buildPagination();
      this._bindEvents();
      this._update();
    }

    _fixLayout() {
      // FIX 1: Remove gap between slides (Dawn flex gap causes bleeding)
      this.wrapper.style.gap = '0';
      this.wrapper.style.columnGap = '0';

      // FIX 2: Wrapper must not scroll — transform handles movement
      this.wrapper.style.overflow = 'visible';
      this.wrapper.style.flexWrap = 'nowrap';

      // FIX 3: Swiper container clips everything outside
      this.el.style.overflow = 'hidden';

      // FIX 4: Each slide must be exactly the swiper width in pixels
      const swiperWidth = this.el.offsetWidth;
      this.slides.forEach(slide => {
        slide.style.flex = `0 0 ${swiperWidth}px`;
        slide.style.width = `${swiperWidth}px`;
        slide.style.minWidth = `${swiperWidth}px`;
      });

      this._slideWidth = swiperWidth;
    }

    _preloadImages() {
      // Force all lazy images in this swiper to load immediately
      this.slides.forEach(slide => {
        slide.querySelectorAll('img').forEach(img => {
          if (!img.complete || img.naturalWidth === 0) {
            img.loading = 'eager';
            const src = img.src;
            if (src) {
              img.src = '';
              img.src = src;
            }
          }
        });
      });
    }

    _buildPagination() {
      if (!this.pagination) return;
      this.pagination.innerHTML = '';
      this.dots = this.slides.map((_, i) => {
        const dot = document.createElement('span');
        dot.className = 'swiper-pagination-bullet';
        dot.setAttribute('aria-label', `Slide ${i + 1}`);
        dot.setAttribute('role', 'button');
        dot.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._goTo(i);
        }, true);
        this.pagination.appendChild(dot);
        return dot;
      });
    }

    _bindEvents() {
      // FIX 5: capture phase + stopPropagation prevents Dawn's full-card <a> from capturing click
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._prev();
        }, true);
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this._next();
        }, true);
      }

      // Touch / swipe support
      let startX = 0;
      let isDragging = false;

      this.el.addEventListener('touchstart', (e) => {
        startX = e.touches[0].clientX;
        isDragging = true;
      }, { passive: true });

      this.el.addEventListener('touchend', (e) => {
        if (!isDragging) return;
        const diff = startX - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 40) {
          e.stopPropagation();
          diff > 0 ? this._next() : this._prev();
        }
        isDragging = false;
      }, { passive: true });

      // Keyboard support
      this.el.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this._prev();
        if (e.key === 'ArrowRight') this._next();
      });

      // Image click → go to product page
      this.slides.forEach(slide => {
        slide.addEventListener('click', (e) => {
          window.location.href = slide.querySelector('a.card__swiper-image-link').href;
        });
      });

      // Recalculate slide width on resize
      window.addEventListener('resize', () => {
        clearTimeout(this._resizeTimer);
        this._resizeTimer = setTimeout(() => {
          this._slideWidth = this.el.offsetWidth;
          this.slides.forEach(slide => {
            slide.style.flex = `0 0 ${this._slideWidth}px`;
            slide.style.width = `${this._slideWidth}px`;
            slide.style.minWidth = `${this._slideWidth}px`;
          });
          this._update();
        }, 150);
      });
    }

    _prev() {
      this._goTo((this.current - 1 + this.total) % this.total);
    }

    _next() {
      this._goTo((this.current + 1) % this.total);
    }

    _goTo(index) {
      if (this.isAnimating || index === this.current) return;
      this.isAnimating = true;
      this.current = index;
      this._update();
      setTimeout(() => { this.isAnimating = false; }, 350);
    }

    _update() {
      // FIX 6: Move by pixel width not percentage — avoids gap offset issues
      this.wrapper.style.transform = `translateX(-${this.current * this._slideWidth}px)`;

      // Update dots
      if (this.dots) {
        this.dots.forEach((dot, i) => {
          dot.classList.toggle('swiper-pagination-bullet-active', i === this.current);
        });
      }

      // Update aria labels
      if (this.prevBtn) this.prevBtn.setAttribute('aria-label', `Previous image (${this.current + 1} of ${this.total})`);
      if (this.nextBtn) this.nextBtn.setAttribute('aria-label', `Next image (${this.current + 1} of ${this.total})`);
    }
  }

  function initSwipers(root = document) {
    root.querySelectorAll('.card__swiper:not([data-swiper-init])').forEach((el) => {
      el.setAttribute('data-swiper-init', 'true');
      new ProductCardSwiper(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initSwipers());
  } else {
    initSwipers();
  }

  document.addEventListener('shopify:section:load', (e) => {
    initSwipers(e.target);
  });

  window.ProductCardSwiper = ProductCardSwiper;
  window.initProductCardSwipers = initSwipers;
})();
